/**
 * Aba Ofertas: produtos e planos da empresa com o interruptor ativo/inativo,
 * filtros com contagem, ordenacao por preco e "ver mais".
 *
 * Decisoes nao obvias:
 *  - O interruptor e estado LOCAL da previa, por id de oferta, com o selo
 *    "Salvo na previa" aparecendo na linha que acabou de mudar. Oferta
 *    inativa continua na lista (apagada), porque o lead que ja esta na regua
 *    dela ainda pode pagar -- sumir com a linha esconderia de onde veio a venda.
 *  - Recorrente e o que alimenta o funil de expirados (assinatura JA vencida).
 *    O topo diz quantas recorrentes ativas existem e se o funil esta ligado:
 *    recorrente com funil desligado e oportunidade parada.
 *  - Empresa com catalogo grande (dezenas de planos): filtros por tipo e
 *    situacao, com contagem, e so as primeiras 12 linhas de cara.
 *  - Abaixo de `md` a tabela vira lista de cartoes (RNF16).
 */

import { useEffect, useState } from 'react';
import { Badge, Button, FilterChips, SectionCard, Toggle, type ChipOption } from '../../../components/ui';
import { PROVIDER_LABEL, formatBRLFromCents, formatCount } from '../../../lib/format';
import type { ProductOffer } from '../../../types';

const SALVO_VISIVEL_MS = 1_800;
const LOTE = 12;

type FiltroTipo = 'todas' | 'recorrente' | 'unica';
type FiltroSituacao = 'todas' | 'ativas' | 'inativas';
type OrdemPreco = 'catalogo' | 'desc' | 'asc';

export function AbaOfertas({ offers, companyName, expiradosLigado }: { readonly offers: readonly ProductOffer[]; readonly companyName: string; readonly expiradosLigado: boolean }): JSX.Element {
  const [ativos, setAtivos] = useState<Readonly<Record<string, boolean>>>({});
  const [salvoId, setSalvoId] = useState<string | null>(null);
  const [tipo, setTipo] = useState<FiltroTipo>('todas');
  const [situacao, setSituacao] = useState<FiltroSituacao>('todas');
  const [ordem, setOrdem] = useState<OrdemPreco>('catalogo');
  const [limite, setLimite] = useState(LOTE);

  useEffect(() => {
    if (salvoId === null) {
      return undefined;
    }
    const timer = window.setTimeout(() => setSalvoId(null), SALVO_VISIVEL_MS);
    return () => window.clearTimeout(timer);
  }, [salvoId]);

  const estaAtiva = (oferta: ProductOffer): boolean => ativos[oferta.id] ?? oferta.active;
  const alternar = (oferta: ProductOffer, proximo: boolean): void => {
    setAtivos((anterior) => ({ ...anterior, [oferta.id]: proximo }));
    setSalvoId(oferta.id);
  };

  const passaTipo = (o: ProductOffer, t: FiltroTipo): boolean => t === 'todas' || (t === 'recorrente' ? o.recurring : !o.recurring);
  const passaSituacao = (o: ProductOffer, s: FiltroSituacao): boolean => s === 'todas' || (s === 'ativas' ? estaAtiva(o) : !estaAtiva(o));

  const filtradas = offers.filter((o) => passaTipo(o, tipo) && passaSituacao(o, situacao));
  const ordenadas = ordem === 'catalogo' ? filtradas : [...filtradas].sort((a, b) => (ordem === 'desc' ? b.priceCents - a.priceCents : a.priceCents - b.priceCents));
  const visiveis = ordenadas.slice(0, limite);

  const totalAtivas = offers.filter(estaAtiva).length;
  const recorrentesAtivas = offers.filter((o) => o.recurring && estaAtiva(o)).length;

  const opcoesTipo: readonly ChipOption<FiltroTipo>[] = [
    { value: 'todas', label: 'Todas', count: formatCount(offers.filter((o) => passaSituacao(o, situacao)).length) },
    { value: 'recorrente', label: 'Recorrentes', count: formatCount(offers.filter((o) => o.recurring && passaSituacao(o, situacao)).length) },
    { value: 'unica', label: 'Compra única', count: formatCount(offers.filter((o) => !o.recurring && passaSituacao(o, situacao)).length) },
  ];
  const opcoesSituacao: readonly ChipOption<FiltroSituacao>[] = [
    { value: 'todas', label: 'Todas', count: formatCount(offers.filter((o) => passaTipo(o, tipo)).length) },
    { value: 'ativas', label: 'Ativas', count: formatCount(offers.filter((o) => passaTipo(o, tipo) && estaAtiva(o)).length) },
    { value: 'inativas', label: 'Inativas', count: formatCount(offers.filter((o) => passaTipo(o, tipo) && !estaAtiva(o)).length) },
  ];

  const proximaOrdem: Readonly<Record<OrdemPreco, OrdemPreco>> = { catalogo: 'desc', desc: 'asc', asc: 'catalogo' };
  const ariaSort = ordem === 'desc' ? 'descending' : ordem === 'asc' ? 'ascending' : 'none';

  const Tipo = ({ oferta }: { readonly oferta: ProductOffer }): JSX.Element => (
    <Badge tone={oferta.recurring ? 'info' : 'neutral'}>{oferta.recurring ? 'Recorrente' : 'Compra única'}</Badge>
  );

  return (
    <SectionCard
      id="ofertas"
      title="Ofertas"
      icon="carrinho"
      description={`${formatCount(totalAtivas)} de ${formatCount(offers.length)} ativas. Recorrentes alimentam o funil de expirados; compra única, o de carrinho abandonado.`}
      padded={false}
    >
      <div className="flex flex-col gap-3 px-5 pb-4">
        <p className={`rounded-lg border px-3 py-2 text-xs ${recorrentesAtivas > 0 && !expiradosLigado ? 'border-info/30 bg-info/5 text-ink' : 'border-line bg-elevated text-ink-muted'}`}>
          {recorrentesAtivas === 0
            ? 'Nenhuma oferta recorrente ativa: o funil de expirados não teria assinatura vencida para recuperar.'
            : expiradosLigado
              ? `${formatCount(recorrentesAtivas)} ${recorrentesAtivas === 1 ? 'recorrente ativa alimenta' : 'recorrentes ativas alimentam'} o funil de expirados, que está ligado: assinatura vencida entra na régua D+15.`
              : `${formatCount(recorrentesAtivas)} ${recorrentesAtivas === 1 ? 'recorrente ativa' : 'recorrentes ativas'} e o funil de expirados desligado: assinaturas que já venceram estão sem régua e sem comissão.`}
        </p>
        {offers.length > 0 ? (
          <div className="flex flex-col gap-2 xl:flex-row xl:items-center xl:justify-between">
            <FilterChips label="Filtrar ofertas por tipo" prefix="Tipo:" options={opcoesTipo} value={tipo} onChange={(v) => { setTipo(v); setLimite(LOTE); }} />
            <FilterChips label="Filtrar ofertas por situação" prefix="Situação:" options={opcoesSituacao} value={situacao} onChange={(v) => { setSituacao(v); setLimite(LOTE); }} tone="accent" />
          </div>
        ) : null}
      </div>

      {offers.length === 0 ? (
        <p className="px-5 pb-8 text-center text-sm text-ink-muted">Nenhuma oferta cadastrada para {companyName}. Sem oferta, a régua não sabe o que está recuperando.</p>
      ) : filtradas.length === 0 ? (
        <div className="flex flex-col items-start gap-2 px-5 pb-6">
          <p className="text-sm text-ink-muted">Nenhuma oferta com esse filtro.</p>
          <Button variant="ghost" size="sm" icon="x" onClick={() => { setTipo('todas'); setSituacao('todas'); }}>
            Limpar filtros
          </Button>
        </div>
      ) : (
        <>
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-y border-line text-left text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">
                  <th scope="col" className="px-5 py-2">
                    Oferta
                  </th>
                  <th scope="col" className="px-3 py-2" aria-sort={ariaSort}>
                    <button type="button" onClick={() => setOrdem((o) => proximaOrdem[o])} className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-ink" title="Ordenar por preço (maior, menor, ordem do catálogo)">
                      Preço
                      <span aria-hidden="true" className={ordem === 'catalogo' ? 'opacity-40' : ''}>
                        {ordem === 'desc' ? '▼' : ordem === 'asc' ? '▲' : '↕'}
                      </span>
                    </button>
                  </th>
                  <th scope="col" className="px-3 py-2">
                    Tipo
                  </th>
                  <th scope="col" className="px-3 py-2">
                    Provedor
                  </th>
                  <th scope="col" className="px-5 py-2 text-right">
                    Ativa
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {visiveis.map((oferta) => {
                  const ativa = estaAtiva(oferta);
                  return (
                    <tr key={oferta.id} className={ativa ? '' : 'text-ink-muted'}>
                      <th scope="row" className="px-5 py-3 text-left font-medium text-ink">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className={ativa ? '' : 'text-ink-muted line-through decoration-ink-subtle'}>{oferta.name}</span>
                          {salvoId === oferta.id ? (
                            <Badge tone="good" icon="check">
                              Salvo na prévia
                            </Badge>
                          ) : null}
                        </span>
                      </th>
                      <td className="px-3 py-3 tabular-nums">{formatBRLFromCents(oferta.priceCents)}</td>
                      <td className="px-3 py-3">
                        <Tipo oferta={oferta} />
                      </td>
                      <td className="px-3 py-3">{PROVIDER_LABEL[oferta.provider]}</td>
                      <td className="px-5 py-3">
                        <div className="flex justify-end">
                          <Toggle checked={ativa} onChange={(proximo) => alternar(oferta, proximo)} label={<span className="sr-only">Oferta {oferta.name} ativa</span>} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <ul className="divide-y divide-line border-t border-line md:hidden">
            {visiveis.map((oferta) => {
              const ativa = estaAtiva(oferta);
              return (
                <li key={oferta.id} className="flex flex-col gap-3 px-5 py-4">
                  <Toggle checked={ativa} onChange={(proximo) => alternar(oferta, proximo)} label={oferta.name} description={`${formatBRLFromCents(oferta.priceCents)} · ${PROVIDER_LABEL[oferta.provider]}`} />
                  <div className="flex flex-wrap items-center gap-2">
                    <Tipo oferta={oferta} />
                    {salvoId === oferta.id ? (
                      <Badge tone="good" icon="check">
                        Salvo na prévia
                      </Badge>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-5 py-3">
            <p className="text-xs text-ink-subtle">
              Mostrando {formatCount(visiveis.length)} de {formatCount(ordenadas.length)}
            </p>
            {visiveis.length < ordenadas.length ? (
              <Button variant="secondary" size="sm" icon="mais" onClick={() => setLimite((n) => n + LOTE)}>
                Ver mais {formatCount(Math.min(LOTE, ordenadas.length - visiveis.length))}
              </Button>
            ) : null}
          </div>
        </>
      )}
    </SectionCard>
  );
}
